import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Trash2, RefreshCw, CheckCircle2, Undo2, ZoomIn } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';
import { MemberPicker } from '../components/MemberSearchPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GroupCard = ({ group, authHeaders, onDone, onZoom }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [member, setMember] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allIds = group.images.map((i) => i.asset_id);
  const targetIds = selectedIds.length ? selectedIds : allIds;

  const post = async (path, body, successMsg) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/media/orphaned-galleries/${path}`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Action failed'); return; }
      toast.success(successMsg(data));
      onDone();
    } catch (e) {
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-stone-200 rounded-lg p-4" data-testid={`recover-group-${group.folder_uuid}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-xs">
          <span className="font-semibold text-stone-800">
            {group.earliest_upload?.slice(0, 10)}
            {group.earliest_upload?.slice(0, 10) !== group.latest_upload?.slice(0, 10)
              ? ` – ${group.latest_upload?.slice(0, 10)}` : ''}
          </span>
          <span className="text-stone-500"> · {group.count} image{group.count !== 1 ? 's' : ''}</span>
          {selectedIds.length > 0 && (
            <span className="ml-2 text-cafa-gold font-medium" data-testid={`recover-selected-count-${group.folder_uuid}`}>
              {selectedIds.length} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selectedIds.length > 0 && (
            <Button size="sm" variant="ghost" className="text-xs h-8"
              onClick={() => setSelectedIds([])}
              data-testid={`recover-clear-selection-${group.folder_uuid}`}>
              Clear
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-xs h-8"
            onClick={() => setSelectedIds(selectedIds.length === allIds.length ? [] : allIds)}
            data-testid={`recover-select-all-${group.folder_uuid}`}>
            {selectedIds.length === allIds.length ? 'Deselect all' : 'Select all'}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-stone-400 hover:text-red-600 h-8"
                title="Hide from this queue (files are never deleted)"
                data-testid={`recover-discard-btn-${group.folder_uuid}`}>
                <Trash2 size={14} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Hide {targetIds.length} image{targetIds.length !== 1 ? 's' : ''}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This only removes them from this review queue. The files stay safely stored and can be
                  brought back with "Restore hidden".
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => post('discard-images', { asset_ids: targetIds }, (d) => `Hid ${d.discarded} image(s)`)}
                  data-testid={`recover-confirm-discard-${group.folder_uuid}`}>
                  Hide
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2 mb-4">
        {group.images.map((img) => {
          const picked = selectedIds.includes(img.asset_id);
          return (
            <div key={img.asset_id} className="relative group">
              <button
                onClick={() => toggle(img.asset_id)}
                className={`block w-full aspect-square rounded overflow-hidden border-2 transition-colors ${picked ? 'border-cafa-gold' : 'border-stone-200 hover:border-stone-400'}`}
                data-testid={`recover-image-${img.asset_id}`}
              >
                <img src={mediaUrl(img, 'thumb')} alt="recovered artwork" className="w-full h-full object-cover" loading="lazy" />
              </button>
              {picked && (
                <CheckCircle2 size={16} className="absolute top-1 left-1 text-white bg-cafa-gold rounded-full" />
              )}
              <button
                onClick={() => onZoom(group, img)}
                className="absolute bottom-1 right-1 bg-white/90 rounded p-1 text-stone-700 opacity-0 group-hover:opacity-100 transition-opacity"
                title="View full size"
                data-testid={`recover-zoom-${img.asset_id}`}
              >
                <ZoomIn size={13} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <MemberPicker
          authHeaders={authHeaders}
          selected={member}
          onPick={setMember}
          testId={`recover-member-search-${group.folder_uuid}`}
        />
        <Button
          size="sm"
          disabled={!member || busy}
          onClick={() => post('claim-images', { asset_ids: targetIds, member_id: member.id },
            (d) => `Attached ${d.claimed} image(s) to ${d.member}'s gallery`)}
          data-testid={`recover-assign-btn-${group.folder_uuid}`}
        >
          {busy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Assigning...</>
            : `Assign ${selectedIds.length ? `${selectedIds.length} selected` : `all ${group.count}`}`}
        </Button>
      </div>
    </div>
  );
};

const AdminRecoverArtwork = () => {
  const { token } = useAuth();
  const authHeaders = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minImages, setMinImages] = useState('1');
  const [sort, setSort] = useState('date_asc');
  const [zoom, setZoom] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort, min_images: minImages });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await fetch(`${API_URL}/api/media/orphaned-galleries?${params.toString()}`, { headers: authHeaders });
      const json = await res.json();
      if (res.ok) setData(json);
      else toast.error(json.detail || 'Could not load the recovery queue');
    } catch (e) {
      toast.error('Could not load the recovery queue');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, sort, minImages, dateFrom, dateTo]);

  const loadJob = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/media/orphaned-galleries/rescan-status`, { headers: authHeaders });
      if (res.ok) setJob(await res.json());
    } catch (e) { /* ignore */ }
  }, [authHeaders]);

  useEffect(() => { if (token) { load(); } }, [token, load]);
  useEffect(() => { if (token) { loadJob(); } }, [token, loadJob]);

  useEffect(() => {
    if (job?.status !== 'running') return;
    const t = setInterval(async () => {
      await loadJob();
    }, 3000);
    return () => clearInterval(t);
  }, [job?.status, loadJob]);

  useEffect(() => {
    if (job?.status === 'done') load();
    // eslint-disable-next-line
  }, [job?.status]);

  const startScan = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/media/orphaned-galleries/rescan`, { method: 'POST', headers: authHeaders });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || 'Could not start the scan'); return; }
      toast.success('Recovery scan started — this can take a few minutes');
      setJob({ status: 'running', processed: 0, total: 0 });
      loadJob();
    } catch (e) {
      toast.error('Network error starting the scan');
    } finally {
      setStarting(false);
    }
  };

  const restoreHidden = async () => {
    const res = await fetch(`${API_URL}/api/media/orphaned-galleries/restore-discarded`, { method: 'POST', headers: authHeaders });
    const json = await res.json();
    if (res.ok) { toast.success(`Restored ${json.restored} hidden image(s)`); load(); }
    else toast.error(json.detail || 'Could not restore');
  };

  const resetFilters = () => { setDateFrom(''); setDateTo(''); setMinImages('1'); setSort('date_asc'); };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-recover-artwork-page">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-stone-900">Recover Member Artwork</h1>
          <p className="text-sm text-stone-500 mt-1 max-w-3xl">
            Artwork recovered from the old image host whose original member profile no longer exists.
            The files are safely stored. Browse by original upload date, click the magnifier to see a piece
            full size, tick the images you recognise, then assign them to the right member.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Images awaiting review', value: data?.unfiltered_total_images ?? '—', testid: 'recover-stat-images' },
            { label: 'Folders (likely members)', value: data?.unfiltered_total_groups ?? '—', testid: 'recover-stat-groups' },
            { label: 'Already assigned', value: data?.claimed_images ?? '—', testid: 'recover-stat-claimed' },
            { label: 'Hidden', value: data?.discarded_images ?? '—', testid: 'recover-stat-hidden' },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="p-4">
                <p className="text-xs text-stone-500">{s.label}</p>
                <p className="text-2xl font-semibold text-stone-900" data-testid={s.testid}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mb-6">
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-stone-500 mb-1">Uploaded from</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 text-xs w-40" data-testid="recover-filter-date-from" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Uploaded to</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-9 text-xs w-40" data-testid="recover-filter-date-to" />
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Minimum images</label>
              <select value={minImages} onChange={(e) => setMinImages(e.target.value)}
                className="h-9 text-xs border border-stone-200 rounded px-2 bg-white"
                data-testid="recover-filter-min-images">
                {['1', '2', '3', '5', '10'].map((v) => <option key={v} value={v}>{v}+</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-500 mb-1">Sort by</label>
              <select value={sort} onChange={(e) => setSort(e.target.value)}
                className="h-9 text-xs border border-stone-200 rounded px-2 bg-white"
                data-testid="recover-filter-sort">
                <option value="date_asc">Oldest upload first</option>
                <option value="date_desc">Newest upload first</option>
                <option value="count_desc">Most images first</option>
                <option value="count_asc">Fewest images first</option>
              </select>
            </div>
            <Button variant="outline" size="sm" className="h-9" onClick={resetFilters} data-testid="recover-reset-filters">
              Reset
            </Button>
            {(data?.discarded_images ?? 0) > 0 && (
              <Button variant="outline" size="sm" className="h-9" onClick={restoreHidden} data-testid="recover-restore-hidden">
                <Undo2 size={14} className="mr-1" /> Restore hidden ({data.discarded_images})
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              {job?.status === 'running' && !job?.stale ? (
                <span className="text-xs text-stone-500 flex items-center gap-1" data-testid="recover-scan-progress">
                  <Loader2 size={13} className="animate-spin" />
                  Scanning {job.processed || 0}/{job.total || '?'}
                </span>
              ) : (
                <Button variant="outline" size="sm" className="h-9" onClick={startScan} disabled={starting}
                  data-testid={job?.stale ? 'recover-restart-scan-btn' : 'recover-run-scan-btn'}>
                  <RefreshCw size={14} className="mr-1" />
                  {starting ? 'Starting...' : job?.stale ? 'Scan stalled — Restart scan' : 'Run recovery scan'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {job?.stale && (
          <p className="text-xs text-amber-600 mb-4" data-testid="recover-scan-stale">
            The last scan stopped reporting progress at {job.processed || 0}/{job.total || '?'} and appears to have
            stalled (likely a server restart mid-scan). It's safe to restart — already-recovered images are
            skipped automatically.
          </p>
        )}
        {job?.status === 'failed' && (
          <p className="text-xs text-red-600 mb-4" data-testid="recover-scan-error">Last scan failed: {job.error}</p>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {loading ? 'Loading…' : `${data?.total_groups || 0} folder(s) · ${data?.total_images || 0} image(s) shown`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-stone-500 py-6">Loading recovered artwork…</p>
            ) : (data?.groups?.length ? (
              <div className="space-y-4">
                {data.groups.map((g) => (
                  <GroupCard key={g.folder_uuid} group={g} authHeaders={authHeaders} onDone={load}
                    onZoom={(grp, img) => setZoom({ group: grp, image: img })} />
                ))}
              </div>
            ) : (
              <div className="py-10 text-center" data-testid="recover-empty-state">
                <p className="text-sm text-stone-600 font-medium">Nothing in the review queue.</p>
                <p className="text-xs text-stone-500 mt-1 max-w-md mx-auto">
                  {(data?.unfiltered_total_images ?? 0) > 0
                    ? 'No folders match the current filters — try Reset.'
                    : 'This database has no recovered artwork yet. Click "Run recovery scan" above to pull the legacy gallery folders in.'}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
          <DialogContent className="max-w-3xl" data-testid="recover-zoom-dialog">
            {zoom && (
              <div>
                <DialogTitle className="sr-only">Recovered artwork preview</DialogTitle>
                <DialogDescription className="sr-only">Full-size preview of the selected recovered artwork image</DialogDescription>
                <img src={zoom.image.url} alt="recovered artwork full size"
                  className="w-full max-h-[70vh] object-contain rounded" />
                <p className="text-xs text-stone-500 mt-3">
                  Originally uploaded {zoom.image.upload_date?.slice(0, 10)} · part of a folder of {zoom.group.count} image(s)
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRecoverArtwork;
